import { useTranslation } from 'react-i18next'
import {
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Dropdown,
  Menu,
  Checkbox,
  Radio,
} from '@arco-design/web-react'
import { IconRefresh, IconSearch, IconSettings } from '@arco-design/web-react/icon'
import type { TaskLogsFilterState, TaskColumnKey } from '@/hooks/useTaskLogs'
import { TASK_COLUMN_KEYS } from '@/hooks/useTaskLogs'

interface TaskLogsFiltersProps {
  filters: TaskLogsFilterState
  onChange: (patch: Partial<TaskLogsFilterState>) => void
  onReset: () => void
  onRefresh: () => void
  isAdmin: boolean
  loading?: boolean
  visibleColumns: TaskColumnKey[]
  onVisibleColumnsChange: (cols: TaskColumnKey[]) => void
  platformOptions: { label: string; value: string }[]
  actionOptions: { label: string; value: string }[]
}

export default function TaskLogsFilters({
  filters,
  onChange,
  onReset,
  onRefresh,
  isAdmin,
  loading,
  visibleColumns,
  onVisibleColumnsChange,
  platformOptions,
  actionOptions,
}: TaskLogsFiltersProps) {
  const { t } = useTranslation()

  const statusOptions = [
    { label: t('tt.task.statusAll'), value: '' },
    { label: t('tt.task.statusSuccess'), value: 'SUCCESS' },
    { label: t('tt.task.statusFailure'), value: 'FAILURE' },
    { label: t('tt.task.statusInProgress'), value: 'IN_PROGRESS' },
    { label: t('tt.task.statusNotStart'), value: 'NOT_START' },
    { label: t('tt.task.statusSubmitted'), value: 'SUBMITTED' },
    { label: t('tt.task.statusQueued'), value: 'QUEUED' },
  ]

  const columnLabels: Record<TaskColumnKey, string> = {
    submit_time: t('tt.task.submitTime'),
    finish_time: t('tt.task.finishTime'),
    duration: t('tt.task.duration'),
    platform: t('tt.task.platform'),
    channel_id: t('tt.task.channel'),
    task_id: t('tt.task.taskId'),
    username: t('tt.task.user'),
    action: t('tt.task.action'),
    status: t('tt.task.status'),
    progress: t('tt.task.progress'),
    result_url: t('tt.task.resultUrl'),
    fail_reason: t('tt.task.failReason'),
  }

  const dateRange: [number, number] | undefined =
    filters.start_timestamp && filters.end_timestamp
      ? [filters.start_timestamp * 1000, filters.end_timestamp * 1000]
      : undefined

  return (
    <div className="space-y-3">
      {isAdmin && (
        <Radio.Group
          type="button"
          value={filters.scope}
          onChange={(v) => onChange({ scope: v })}
          options={[
            { label: t('tt.task.scopeSelf'), value: 'self' },
            { label: t('tt.task.scopeAll'), value: 'all' },
          ]}
        />
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
        <Input
          placeholder={t('tt.task.taskIdPlaceholder')}
          value={filters.task_id}
          onChange={(v) => onChange({ task_id: v })}
          className="w-full sm:w-[220px]"
          prefix={<IconSearch />}
          allowClear
        />

        <Select
          placeholder={t('tt.task.platform')}
          value={filters.platform ?? ''}
          onChange={(v) => onChange({ platform: v || undefined })}
          options={[{ label: t('tt.task.platformAll'), value: '' }, ...platformOptions]}
          className="w-full sm:w-[140px]"
          allowClear
        />

        <Select
          placeholder={t('tt.task.action')}
          value={filters.action ?? ''}
          onChange={(v) => onChange({ action: v || undefined })}
          options={[{ label: t('tt.task.actionAll'), value: '' }, ...actionOptions]}
          className="w-full sm:w-[140px]"
          allowClear
        />

        <Select
          placeholder={t('tt.task.status')}
          value={filters.status ?? ''}
          onChange={(v) => onChange({ status: v || undefined })}
          options={statusOptions}
          className="w-full sm:w-[140px]"
        />

        {isAdmin && filters.scope === 'all' && (
          <InputNumber
            placeholder={t('tt.task.channelId')}
            value={filters.channel_id}
            onChange={(v) => onChange({ channel_id: v === undefined ? undefined : Number(v) })}
            className="w-full sm:w-[120px]"
            min={0}
          />
        )}

        <DatePicker.RangePicker
          showTime
          value={dateRange}
          onChange={(values) => {
            if (!values || !values[0] || !values[1]) {
              onChange({ start_timestamp: undefined, end_timestamp: undefined })
              return
            }
            onChange({
              start_timestamp: Math.floor(new Date(values[0] as string).getTime() / 1000),
              end_timestamp: Math.floor(new Date(values[1] as string).getTime() / 1000),
            })
          }}
          className="w-full sm:w-[360px]"
        />

        <div className="flex-1 min-w-[8px]" />

        <Button onClick={onReset}>{t('重置')}</Button>
        <Button icon={<IconRefresh />} onClick={onRefresh} loading={loading} aria-label={t('刷新')}>
          {t('刷新')}
        </Button>

        <Dropdown
          trigger="click"
          droplist={
            <Menu style={{ minWidth: 180 }}>
              {TASK_COLUMN_KEYS.map((key) => (
                <Menu.Item key={key}>
                  <Checkbox
                    checked={visibleColumns.includes(key)}
                    onChange={(checked) => {
                      if (checked) {
                        onVisibleColumnsChange(
                          TASK_COLUMN_KEYS.filter(
                            (k) => visibleColumns.includes(k) || k === key,
                          ),
                        )
                      } else {
                        onVisibleColumnsChange(visibleColumns.filter((k) => k !== key))
                      }
                    }}
                  >
                    {columnLabels[key]}
                  </Checkbox>
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button icon={<IconSettings />} aria-label={t('tt.task.columns')}>
            {t('tt.task.columns')}
          </Button>
        </Dropdown>
      </div>

      {(platformOptions.length > 0 || actionOptions.length > 0) && (
        <p className="text-[12px] text-[var(--console-text-subtle)]">{t('tt.task.optionsFromCurrentPage')}</p>
      )}
    </div>
  )
}
